import { cn } from '@/lib/utils';
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { TruncatedText } from '@/components/common/truncated-text';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { IconButton } from '@/components/common/icon-button';
import { Pagination } from '@/components/common/pagination';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { Image } from '@/components/common/image';
import { CircleAlert, Eye } from 'lucide-react';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { ContentDetailModal } from '@/components/modal-content-detail';
import { PushContentData } from '@/services/agent-v2-push-content-list';
import { agentContentHooks } from '@/manager';
import { AiChatTable } from './ai-chat-table';
interface TableListProps {
  contentList: PushContentData[];
  loading: boolean;
  total: number;
  page: number;
  type: number;
  pageSize: number;
  changePage: (page: number) => void;
  search: () => void;
}

/** 智能体内容管理-内容表格列表 */
export const ContentTableList = ({
  contentList,
  loading,
  total,
  page,
  type,
  pageSize,
  changePage,
  search,
}: TableListProps) => {
  const { t } = useTranslation();
  const [showContentDetailModal, setShowContentDetailModal] = useState(false);
  const {
    loading: detailLoading,
    fetchDetail,
    contentDetail,
    resetDetail,
  } = agentContentHooks.useContentDetail();

  // Ai对话类型
  const isAiChat = [7].includes(type);
  
  const handleSizeChange = (page: number) => {
    changePage(page);
    search();
  }

  const handleDetail = (id: string) => {
    try {
      fetchDetail(id);
      setShowContentDetailModal(true);
    } catch (error) {
      console.error('Error fetching content detail:', error);
    }
  }

  const typeMap = {
    '1': t('agent_dashboard.content_management.table.type_map.video'),
    '2': t('agent_dashboard.content_management.table.type_map.podcast'),
    '3': t('agent_dashboard.content_management.table.type_map.moments'),
    '4': t('agent_dashboard.content_management.table.type_map.image'),
    '5': t('agent_dashboard.content_management.table.type_map.article'),
  }

  // Ai创作内容类型
  const createSourceMap = {
    'AI-video': t('agent_dashboard.content_management.table.create_source_map.ai_video'),
    'AI-pic': t('agent_dashboard.content_management.table.create_source_map.ai_image'),
  }

  const sourceMap = {
    '1': t('agent_dashboard.content_management.search.source_map.user'),
    '0': t('agent_dashboard.content_management.search.source_map.auto'),
  }

  const statusMap = {
    '1': t('agent_dashboard.content_management.table.status_map.viewed'),
    '2': t('agent_dashboard.content_management.table.status_map.pushed'),
    '3': t('agent_dashboard.content_management.table.status_map.pending_push'),
    '0': t('agent_dashboard.content_management.table.status_map.push_failed'),
  }

  const newStatusMap = {
    '0': t('agent_dashboard.content_management.search.new_status.processing'),
    '1': t('agent_dashboard.content_management.search.new_status.finished'),
    '2': t('agent_dashboard.content_management.search.new_status.failed'),
    '3': t('agent_dashboard.content_management.search.new_status.cancelled'),
    '9': t('agent_dashboard.content_management.search.new_status.deleted'),
    '10': t('agent_dashboard.content_management.search.new_status.timeout'),
  }

  if (isAiChat) {
    return (
      <AiChatTable
        contentList={contentList}
        loading={loading}
        total={total}
        page={page}
        pageSize={pageSize}
        changePage={changePage}
        search={search}
      />
    );
  }

  return (
    <div className='relative flex-1 flex flex-col rounded-lg border overflow-hidden'>
      <div
        className="overflow-auto flex-1"
      >
        <table className="w-full rounded-t-lg relative">
          <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
            <tr className="bg-gray-50">
              <Th>{t('agent_dashboard.content_management.table.thumbnail')}</Th>
              <Th>
                {t('agent_dashboard.content_management.table.user_nickname')}/
                {t('agent_dashboard.content_management.table.id')}
              </Th>
              <Th>{t('agent_dashboard.content_management.table.content_title')}</Th>
              <Th>{t('agent_dashboard.content_management.table.type')}</Th>
              <Th>{t('agent_dashboard.content_management.table.source')}</Th>
              <Th>{t('agent_dashboard.content_management.table.push_time')}</Th>
              {/* <Th>{t('agent_dashboard.content_management.table.view_time')}</Th> */}
              <Th>{t('agent_dashboard.content_management.table.status')}</Th>
              <Th className='sticky right-0 bg-gray-50 min-w-[7rem] max-w-28 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]'>
                {t('agent_dashboard.content_management.table.action')}
              </Th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            {contentList?.length > 0 && contentList?.map((item, index) => (
              <tr key={`${index}`} className='border-b h-18 transition-colors duration-200'>
                <Td className='max-w-24 py-1'>
                  {item.coverImageUrl ? <div className='w-12 h-16 overflow-hidden mr-3 flex-shrink-0'>
                    <Image src={item.coverImageUrl} className="size-full rounded-sm object-cover" />
                  </div> : '-'}
                </Td>
                <Td className='min-w-[8rem]'>
                  <div>
                    <TruncatedText text={item.userName} className='text-black pb-1' />
                    <div className='flex items-center'>
                      <TruncatedText text={item.userId.toString()} className='mr-1' />
                      <CopyClipboard text={item.userId.toString()} />
                    </div>
                  </div>
                </Td>
                <Td >
                  {[1, 3].includes(item.type) ? item.content ? <div className='flex items-center'>
                    <TruncatedText text={item.content} className='mr-1' />
                    <CopyClipboard text={item.content} />
                  </div> : '-' : ''}
                  {[2, 4, 5, 6].includes(item.type) ? item.title ? <div className='flex items-center'>
                    <TruncatedText text={item.title} className='mr-1' />
                    <CopyClipboard text={item.title} />
                  </div> : '-' : ''}
                </Td>
                <Td className=''>
                  <span className={cn(
                    "py-0.5 px-2 rounded-lg text-sm font-semibold",
                    item.type === 1 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                    item.type === 2 && "text-[rgba(89,37,220,1)] bg-[rgba(244,243,255,1)]",
                    item.type === 3 && "text-[rgba(181,71,8,1)] bg-[rgba(255,250,235,1)]",
                    item.type === 4 && "text-[rgba(193,21,116,1)] bg-[rgba(253,242,250,1)]",
                    item.type === 5 && "text-[rgba(6,118,118,1)] bg-[rgba(238,254,255,1)]",
                    item.type === 6 && item.createSource === "AI-video" && "text-[rgba(2,106,162,1)] bg-[rgba(240,249,255,1)]",
                    item.type === 6 && item.createSource === "AI-pic" && "text-[rgba(192,16,72,1)] bg-[rgba(255,241,243,1)]",
                  )}>
                    {item.type !== 6 && typeMap[item.type]}
                    {item.type === 6 && createSourceMap[item.createSource]}
                  </span>
                </Td>
                <Td>
                  {sourceMap[item.source.toString()]}
                </Td>
                <Td className='min-w-[7rem]'>{formatToFullDateTime(item.pushTime * 1000)}</Td>
                {/* <Td className='min-w-[7rem]'>{formatToFullDateTime(item.readTime * 1000)}</Td> */}
                <Td className='min-w-[8rem] flex-shrink-0'>
                  <div className={cn(
                    "flex max-w-max items-center justify-between py-1 px-2 rounded-full font-medium text-sm",
                    item.status === 0 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                    item.status === 1 && "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                    item.status === 3 && "bg-[#F2F4F7]",
                    item.status === 9 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                    [2, 10].includes(item.status) && "text-[rgba(192,16,72,1)] bg-[rgba(255,241,243,1)]",
                  )}>
                    <span className='text-sm'>{newStatusMap[item.status] ?? item.status}</span>
                    {item.status === 2 && <CircleAlert className='size-4 text-[rgba(192,16,72,1)] ml-1' />}
                  </div>
                </Td>
                <Td className='sticky right-0 bg-white min-w-[5rem] max-w-28 z-[5]  group-hover:bg-gray-50 space-x-5 flex items-center py-4 h-full'>
                  <IconButton
                    className='size-10 rounded-[8px]'
                    icon={<Eye className='size-6' />}
                    onClick={() => {
                      handleDetail(item.id?.toString());
                      setShowContentDetailModal(true);
                    }}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='h-[4.25rem] border-t border-[rgba(234,236,240,1)] px-6 flex items-center'>
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handleSizeChange}
        />
      </div>
      <LoadingOverlay loading={loading} />
      <ContentDetailModal
        open={showContentDetailModal}
        onOpenChange={(open) => {
          setShowContentDetailModal(open);
          !open && resetDetail();
        }}
        loading={detailLoading}
        details={contentDetail}
      />
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6  text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem]  pl-6 text-sm font-medium  text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)